// src/pages/Dashboard.js
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDataContext } from '../contexts/DataContext';
import Tooltip from '../components/Tooltip';

function getApprovalTypeLabel(approvalType) {
  switch (approvalType) {
    case 'statewide': return 'Statewide';
    case 'regional': return 'Regional';
    case 'local': return 'Local (Not Approved)';
    default: return 'Unknown';
  }
}

function Dashboard() {
  const { students, programs, schoolYears, loading } = useDataContext();

  const stats = useMemo(() => {
    const uniqueCompleters = new Set();
    const uniquePotential = new Set();
    const uniqueConcentrators = new Set();
    const uniqueExplorers = new Set();
    const uniqueParticipants = new Set();

    const programCompletionStats = programs.map(program => {
      let completers = 0;
      let potentialCompleters = 0;
      let concentrators = 0;
      let explorers = 0;
      let participants = 0;
      let studentsInProgram = 0;

      students.forEach(student => {
        const status = student.programStatus[program.id];
        if (!status) return;
        if (status.coursesTaken && status.coursesTaken.length > 0) studentsInProgram++;

        if (status.isCompleter) {
          completers++;
          uniqueCompleters.add(student.id);
        } else if (status.willBeCompleter) {
          potentialCompleters++;
          uniquePotential.add(student.id);
        } else if (status.isConcentrator) {
          concentrators++;
          uniqueConcentrators.add(student.id);
        } else if (status.isExplorer) {
          explorers++;
          uniqueExplorers.add(student.id);
        } else if (status.isParticipant) {
          participants++;
          uniqueParticipants.add(student.id);
        }
      });

      const completionRate = studentsInProgram > 0
        ? ((completers / studentsInProgram) * 100).toFixed(1)
        : '0.0';
      const conversionRate = studentsInProgram > 0
        ? (((completers + potentialCompleters) / studentsInProgram) * 100).toFixed(1)
        : '0.0';

      return {
        programId: program.id,
        programName: program.name,
        approvalType: program.approvalType,
        completers,
        potentialCompleters,
        concentrators,
        explorers,
        participants,
        studentsInProgram,
        percentage: students.length > 0
          ? ((completers / students.length) * 100).toFixed(1)
          : '0.0',
        programPercentage: completionRate,
        conversionRate
      };
    });

    // Per-school-year upload counts — derived from schoolYears the data context
    // already surfaced; zero-safe without re-querying Firestore.
    const uploads = schoolYears.reduce((acc, y) => {
      acc[y] = students.filter(s => s.years && s.years[y]).length;
      return acc;
    }, {});

    return {
      totalStudents: students.length,
      totalPrograms: programs.length,
      totalCompleters: uniqueCompleters.size,
      totalPotentialCompleters: uniquePotential.size,
      totalConcentrators: uniqueConcentrators.size,
      totalExplorers: uniqueExplorers.size,
      totalParticipants: uniqueParticipants.size,
      uploads,
      programCompletionStats
    };
  }, [students, programs, schoolYears]);

  if (loading) return <div>Loading dashboard data...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CTE Completer Dashboard</h1>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <h2 className="text-lg font-semibold mb-2">Quick Start Guide</h2>
        <ol className="list-decimal list-inside space-y-1">
          <li>Upload student spreadsheets for each school year</li>
          <li>Define your programs of study and mark which courses are upper level (Level 3/4)</li>
          <li>View student analysis to see who meets CTE Completer requirements</li>
          <li>Generate reports by program or completion status</li>
        </ol>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border rounded p-4 bg-white">
          <h2 className="text-lg font-semibold mb-3">Data Upload Status</h2>
          {schoolYears.length === 0 ? (
            <p className="text-gray-500">No data has been uploaded yet.</p>
          ) : (
            <ul className="space-y-2">
              {schoolYears.map(year => (
                <li key={year} className="flex items-center">
                  <span className={`inline-block w-5 h-5 rounded-full mr-2 ${stats.uploads[year] ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  School Year {year}: {stats.uploads[year] ? `${stats.uploads[year]} students` : 'No students'}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4">
            <Link to="/upload" className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              {Object.keys(stats.uploads).length > 0 ? 'Manage Uploads' : 'Upload Data'}
            </Link>
          </div>
        </div>

        <div className="border rounded p-4 bg-white">
          <h2 className="text-lg font-semibold mb-3">Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Total Students</p>
              <p className="text-2xl font-bold">{stats.totalStudents}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Programs of Study</p>
              <p className="text-2xl font-bold">{stats.totalPrograms}</p>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <p className="text-sm text-gray-600">
                CTE Completers
                <Tooltip text="Students who have completed 3+ courses, 4+ credits, and at least 1 upper level course in an approved program" />
              </p>
              <p className="text-2xl font-bold text-green-700">{stats.totalCompleters}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded">
              <p className="text-sm text-gray-600">
                Potential Completers
                <Tooltip text="Students who will meet all completer requirements once current in-progress courses are completed" />
              </p>
              <p className="text-2xl font-bold text-blue-700">{stats.totalPotentialCompleters}</p>
            </div>
          </div>

          <h3 className="font-semibold mt-4 mb-2">CTE Indicators</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-2 bg-blue-50 rounded">
              <p className="text-sm text-gray-600">
                Concentrators
                <Tooltip text="Students who have completed 2+ courses for 2+ credits in the same approved program" />
              </p>
              <p className="text-xl font-bold text-blue-700">{stats.totalConcentrators}</p>
            </div>
            <div className="p-2 bg-purple-50 rounded">
              <p className="text-sm text-gray-600">
                Explorers
                <Tooltip text="Students taking 2+ courses for 2+ credits across programs, or students who would be completers/concentrators in a non-approved regional program" />
              </p>
              <p className="text-xl font-bold text-purple-700">{stats.totalExplorers}</p>
            </div>
            <div className="p-2 bg-orange-50 rounded">
              <p className="text-sm text-gray-600">
                Participants
                <Tooltip text="Students who have taken one course or multiple courses for less than 2 credits total" />
              </p>
              <p className="text-xl font-bold text-orange-700">{stats.totalParticipants}</p>
            </div>
          </div>

          <div className="mt-4">
            <Link to="/programs" className="inline-block bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mr-2">
              Manage Programs
            </Link>
            <Link to="/reports" className="inline-block bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">
              View Reports
            </Link>
          </div>
        </div>
      </div>

      <div className="border rounded p-4 bg-white">
        <h2 className="text-lg font-semibold mb-3">
          Program Completion Overview
          <Tooltip text="Overview of completion status for each program. Hover over column headers for more information." />
        </h2>
        {stats.programCompletionStats.length === 0 ? (
          <p className="text-gray-500">No program data available. Please upload student data and define programs.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left">Program</th>
                  <th className="border px-4 py-2 text-center">
                    Approval Type
                    <Tooltip text="Statewide: approved across Texas; Regional: approved for specific regions; Local: not regionally approved" />
                  </th>
                  <th className="border px-4 py-2 text-center">Students in Program</th>
                  <th className="border px-4 py-2 text-center">Completers</th>
                  <th className="border px-4 py-2 text-center">Future Completers</th>
                  <th className="border px-4 py-2 text-center">Program Completion %</th>
                  <th className="border px-4 py-2 text-center">Conversion Rate</th>
                  <th className="border px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.programCompletionStats.map((program, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border px-4 py-2">{program.programName}</td>
                    <td className="border px-4 py-2 text-center">{getApprovalTypeLabel(program.approvalType)}</td>
                    <td className="border px-4 py-2 text-center">{program.studentsInProgram}</td>
                    <td className="border px-4 py-2 text-center">{program.completers}</td>
                    <td className="border px-4 py-2 text-center">{program.potentialCompleters}</td>
                    <td className="border px-4 py-2 text-center">{program.programPercentage}%</td>
                    <td className="border px-4 py-2 text-center">{program.conversionRate}%</td>
                    <td className="border px-4 py-2 text-center">
                      <Link
                        to={`/students?program=${program.programId}`}
                        className="inline-block bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        View Students
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border rounded p-4 bg-white mt-6">
        <h2 className="text-lg font-semibold mb-3">
          CTE Indicators by Program
          <Tooltip text="Breakdown of student counts by CTE indicator code for each program" />
        </h2>
        {stats.programCompletionStats.length === 0 ? (
          <p className="text-gray-500">No program data available. Please upload student data and define programs.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left">Program</th>
                  <th className="border px-4 py-2 text-center bg-green-50">Completers (7)</th>
                  <th className="border px-4 py-2 text-center bg-blue-50">Concentrators (6)</th>
                  <th className="border px-4 py-2 text-center bg-purple-50">Explorers (E)</th>
                  <th className="border px-4 py-2 text-center bg-orange-50">Participants (5)</th>
                  <th className="border px-4 py-2 text-center">Total Students</th>
                </tr>
              </thead>
              <tbody>
                {stats.programCompletionStats.map((program, index) => {
                  const pct = n => program.studentsInProgram > 0
                    ? ((n / program.studentsInProgram) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border px-4 py-2">{program.programName}</td>
                      <td className="border px-4 py-2 text-center bg-green-50">
                        {program.completers}
                        <span className="text-sm text-gray-500 ml-1">({pct(program.completers)}%)</span>
                      </td>
                      <td className="border px-4 py-2 text-center bg-blue-50">
                        {program.concentrators}
                        <span className="text-sm text-gray-500 ml-1">({pct(program.concentrators)}%)</span>
                      </td>
                      <td className="border px-4 py-2 text-center bg-purple-50">
                        {program.explorers}
                        <span className="text-sm text-gray-500 ml-1">({pct(program.explorers)}%)</span>
                      </td>
                      <td className="border px-4 py-2 text-center bg-orange-50">
                        {program.participants}
                        <span className="text-sm text-gray-500 ml-1">({pct(program.participants)}%)</span>
                      </td>
                      <td className="border px-4 py-2 text-center">{program.studentsInProgram}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
